import type { GateResult } from '../types'

// 延遲函數
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// =============================================================================
// 路由切換自動取消 (per-pathname AbortController)
// -----------------------------------------------------------------------------
// 動機：使用者快速切換頁面時，前頁的 GET 請求還在飛、新頁的 API 又堆上，
// 後端 single-thread event loop 跟連線池被過期請求佔用，新請求看起來「卡住」。
// 機制：每個 pathname 對應一個 AbortController；getNavSignal 偵測到 pathname
// 變動就取消上一個 controller，新請求自動拿到新 signal。
// 只用在 GET — POST/PUT/PATCH/DELETE 不取消（避免使用者切頁時把儲存中的
// 請求斷掉、造成資料遺失）。
// =============================================================================

const _navControllers = new Map<string, AbortController>()
let _currentPath: string = typeof window !== 'undefined' ? window.location.pathname : ''

const getCurrentPath = (): string =>
  typeof window !== 'undefined' ? window.location.pathname : ''

export function getNavSignal(): AbortSignal {
  const path = getCurrentPath()

  // pathname 變了 → 取消舊 controller、清掉
  if (path !== _currentPath) {
    const old = _navControllers.get(_currentPath)
    if (old) {
      old.abort()
      _navControllers.delete(_currentPath)
    }
    _currentPath = path
  }

  if (!_navControllers.has(path)) {
    _navControllers.set(path, new AbortController())
  }
  return _navControllers.get(path)!.signal
}

/** 主動取消當前頁所有 in-flight GET（例：登出、Auth 失敗） */
export function cancelNavRequests(): void {
  const path = getCurrentPath()
  const ctrl = _navControllers.get(path)
  if (ctrl) {
    ctrl.abort()
    _navControllers.delete(path)
  }
  _currentPath = path
}

export function isAbortError(err: unknown): boolean {
  if (!err) return false
  if (err instanceof DOMException && err.name === 'AbortError') return true
  if (err instanceof Error && err.name === 'AbortError') return true
  return false
}

const isGetMethod = (options?: RequestInit): boolean =>
  !options?.method || options.method.toUpperCase() === 'GET'

/**
 * fetch 包裝：GET 請求自動帶上 nav signal；POST/PUT/PATCH/DELETE 維持原樣。
 * 已指定 signal 的不覆蓋（呼叫端可以傳自己的 controller）。
 * 切頁取消時：直接 throw AbortError；caller 用 isAbortError(err) 過濾即可，
 * 避免 Promise.all 因為一個 abort 全部 hang 住。
 */
export const apiFetch = (url: string, options: RequestInit = {}): Promise<Response> => {
  const opts = (isGetMethod(options) && !options.signal)
    ? { ...options, signal: getNavSignal() }
    : options
  return fetch(url, opts)
}

/** 429 最多重試幾次。見下方說明：重試次數越多，被限流時對伺服器的加壓越大 */
const MAX_429_RETRIES = 1

// 重試機制
export const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error

      const is429 = error instanceof Error && error.message.includes('429')

      // ⛔ 429 只重試一次（而非跟著 maxRetries 重試 3 次）：
      // 這支包住了全站每一支 API，一旦有人踩到限流，每個請求都自動再打 3 次，
      // 等於瞬間把流量放大 4 倍、讓配額更難恢復——使用者體感就是「越急著重整越卡」。
      // 等待時間優先採用伺服器回的 RateLimit-Reset / Retry-After（後端已開 standardHeaders），
      // 比盲目指數退避準確，也不會白等。
      if (is429 && attempt < Math.min(maxRetries, MAX_429_RETRIES)) {
        const serverHint = (error as ApiError).retryAfterMs
        const delayTime = serverHint ?? baseDelay * Math.pow(2, attempt)
        console.warn(`API rate limited, retrying in ${delayTime}ms (server hint: ${serverHint ?? 'none'})`)
        await delay(delayTime)
        continue
      }

      // 其他錯誤或已達最大重試次數，直接拋出
      throw error
    }
  }

  throw lastError!
}

// API 錯誤類別（攜帶 HTTP status）
export class ApiError extends Error {
  status: number
  errors: string[]
  // 423（計時器被 gate 鎖定）時後端會多帶一個 gate 物件，見 gate-contract.md §3
  gate?: GateResult
  /** 429 時從 RateLimit-Reset / Retry-After 換算出的建議等待毫秒數，供 withRetry 使用 */
  retryAfterMs?: number
  /** 後端回的錯誤代碼（例如 SESSION_REVOKED、MFA_INVALID_CODE），沒有就是 undefined */
  code?: string
  constructor(message: string, status: number, errors: string[] = [], gate?: GateResult, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errors = errors
    this.gate = gate
    this.code = code
  }
}

// 處理 API 回應
export const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const errorMessage = errorData.errors?.[0] || errorData.message || `HTTP error! status: ${response.status}`
    const errorCode = typeof errorData.code === 'string' ? errorData.code : undefined

    // 為 429 錯誤添加特殊標記（保持向下相容）
    if (response.status === 429) {
      const rateLimitError = new ApiError(`429: ${errorMessage}`, 429, errorData.errors, undefined, errorCode)
      // 後端已開 standardHeaders：RateLimit-Reset 是「還要幾秒才重置」。
      // 讓 withRetry 照伺服器說的等，比盲目指數退避準確。上限 60 秒避免卡太久。
      const hint = response.headers.get('Retry-After') ?? response.headers.get('RateLimit-Reset')
      const hintSeconds = hint ? Number(hint) : NaN
      if (Number.isFinite(hintSeconds) && hintSeconds >= 0) {
        rateLimitError.retryAfterMs = Math.min(hintSeconds * 1000, 60_000)
      }
      throw rateLimitError
    }

    // 為 401 錯誤添加特殊標記
    if (response.status === 401) {
      throw new ApiError(`401: ${errorMessage}`, 401, errorData.errors, undefined, errorCode)
    }

    throw new ApiError(errorMessage, response.status, errorData.errors, errorData.gate, errorCode)
  }
  return response.json()
}

// 取得認證標頭
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  }
}

// API 基礎 URL
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001'

// 時區偵測和處理函數
export const getTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset()
}

export const getTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    console.warn('Cannot detect timezone, using offset instead:', error)
    return `UTC${getTimezoneOffset() > 0 ? '-' : '+'}${Math.abs(getTimezoneOffset() / 60)}`
  }
}

// 在 API 請求中添加時區信息
export const addTimezoneToParams = (params: URLSearchParams): URLSearchParams => {
  params.set('timezoneOffset', getTimezoneOffset().toString())
  params.set('timezone', getTimezone())
  return params
}

// 在 API 請求中添加時區信息到查詢字符串
export const addTimezoneToUrl = (url: string): string => {
  // 處理相對路徑，避免 new URL() 的問題
  const searchParams = new URLSearchParams()
  
  // 解析現有的查詢參數（如果有的話）
  const [path, existingQuery] = url.split('?')
  if (existingQuery) {
    const existing = new URLSearchParams(existingQuery)
    existing.forEach((value, key) => {
      searchParams.append(key, value)
    })
  }
  
  // 添加時區信息，確保正確編碼
  searchParams.set('timezoneOffset', getTimezoneOffset().toString())
  searchParams.set('timezone', encodeURIComponent(getTimezone()))
  
  return `${path}?${searchParams.toString()}`
}

/** 續期暫時失敗時給使用者看的訊息：登入還在，稍後再試 */
export const SESSION_RETRY_MESSAGE = '暫時無法連線到會員中樞，登入狀態還在，請稍後再試'
/** 續期確定失敗（登入已失效）時的訊息 */
export const SESSION_EXPIRED_MESSAGE = '登入已過期，請重新登入'

/**
 * 續期（/auth/refresh）失敗。
 * retryable = true：後端或會員中樞暫時連不上（5xx、429、網路錯誤），登入還在，不可以清 token、不可以登出；
 * retryable = false：登入確定失效（401／403、沒有 refresh token），要重新登入。
 */
export class TokenRefreshError extends Error {
  readonly retryable: boolean
  constructor(message: string, retryable: boolean) {
    super(message)
    this.name = 'TokenRefreshError'
    this.retryable = retryable
  }
}

export const isRetryableRefreshError = (error: unknown): boolean =>
  error instanceof TokenRefreshError && error.retryable

/** 登入確定失效：清掉 token，通知 AuthContext 登出（ProtectedRoute 會導去登入頁） */
const clearTokensAndForceLogout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  window.dispatchEvent(new CustomEvent('auth:forceLogout'))
}

// Token 刷新去重鎖：多個 401 同時發生時，只執行一次 refresh
let refreshPromise: Promise<string> | null = null

/** 跨分頁續期鎖的名稱（Web Locks API，同一個網域的分頁共用） */
const REFRESH_LOCK_NAME = 'anytime-token-refresh'
/** 單次續期最多等多久：續期在跨分頁鎖裡跑，不能因為一支卡住的請求讓其他分頁一直等 */
const REFRESH_TIMEOUT_MS = 30_000

/**
 * 跨分頁排隊：同一個瀏覽器的多個分頁同時續期時，一次只有一個「讀 token → 打 /auth/refresh → 存 token」。
 * 只靠「存之前比一下」不夠：比完到存之間別的分頁可能剛好存了更新的一組，被舊的蓋掉後，
 * 下次續期用的是超過寬限期的舊 refresh token，後端會當成外流把整筆登入作廢。
 * 不支援 Web Locks 的瀏覽器退回不排隊（仍有存之前的比對）。
 */
const withCrossTabRefreshLock = async <T,>(task: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks || typeof locks.request !== 'function') return task()
  return locks.request(REFRESH_LOCK_NAME, task)
}

// 導出供 authedRawFetch（下方，給 FormData 上傳／blob 下載用）共用同一把鎖：
// 多支請求各自拿舊 refresh token 去換會互相把對方換掉。
// 失敗時丟 TokenRefreshError，用 isRetryableRefreshError 分辨要不要登出。
export const refreshAccessTokenWithLock = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise

  // 記下排隊前的 refresh token：等到跨分頁鎖時如果已經被別的分頁換掉，直接用新的，不再換一次
  const expectedRefreshToken = localStorage.getItem('refreshToken')
  refreshPromise = withCrossTabRefreshLock(() => refreshAccessToken(expectedRefreshToken)).finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

/**
 * 收到 401 之後要續期的地方都用這支（apiRequestWithAutoRefresh、authedRawFetch、各 service 的上傳下載）：
 * 共用同一把鎖、一起存新的 refresh token。中樞暫時連不上時丟 503 的 ApiError（不登出），
 * 登入確定失效才清 token 並通知登出。
 */
export const refreshAfterUnauthorized = async (): Promise<string> => {
  try {
    return await refreshAccessTokenWithLock()
  } catch (refreshError) {
    // 路由切換造成的 abort 不算「refresh 失敗」：不要清 token、不要強制登出
    if (isAbortError(refreshError)) throw refreshError
    if (isRetryableRefreshError(refreshError)) {
      console.warn('Token refresh temporarily unavailable, keeping session')
      throw new ApiError(SESSION_RETRY_MESSAGE, 503, [SESSION_RETRY_MESSAGE], undefined, 'SESSION_REFRESH_UNAVAILABLE')
    }
    console.error('Token refresh failed:', refreshError)
    clearTokensAndForceLogout()
    throw new Error(SESSION_EXPIRED_MESSAGE)
  }
}

/**
 * 收到 401 之後要用哪張 access token 重送（sentToken 是送出那次請求時用的）：
 * 本地已經換成新的一張（別的請求或分頁剛續期過）就直接用新的，不要再續期一次；
 * refresh token 每次續期都換新，多換的那一次會讓還在路上的舊 refresh token 過了寬限期被當成外流。
 * 本地的就是送出時那張（真的過期了）才續期，規則同 refreshAfterUnauthorized。
 */
export const tokenAfterUnauthorized = async (sentToken: string | null | undefined): Promise<string> => {
  const latest = localStorage.getItem('token')
  if (latest && latest !== sentToken) return latest
  return refreshAfterUnauthorized()
}

/** 從 getAuthHeaders() 組出來的標頭取回這次送出的 access token */
const bearerTokenOf = (headers: HeadersInit): string | null => {
  const value = (headers as Record<string, string | undefined>).Authorization
  return typeof value === 'string' && value.startsWith('Bearer ') ? value.slice(7) : null
}

// 刷新Token的函數（在跨分頁鎖裡跑，見 withCrossTabRefreshLock）
const refreshAccessToken = async (expectedRefreshToken: string | null = null): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken')

  if (!refreshToken) {
    throw new TokenRefreshError('No refresh token available', false)
  }

  // 排隊等鎖的時候別的分頁已經換好一組：直接用，不再換一次
  if (expectedRefreshToken && refreshToken !== expectedRefreshToken) {
    const latestAccessToken = localStorage.getItem('token')
    if (latestAccessToken) return latestAccessToken
  }

  // 逾時要涵蓋到讀完回應內容：標頭到了、內容卡住時 fetch 已經回來，只清計時器的話後面讀內容會一直等，
  // 跨分頁鎖也跟著一直佔著，其他分頁都不能續期。逾時就不再等（同時中止請求），這次的結果一律不存
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer: ReturnType<typeof setTimeout> | null = null
  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new TokenRefreshError(SESSION_RETRY_MESSAGE, true))
      controller?.abort()
    }, REFRESH_TIMEOUT_MS)
  })
  const exchange = async (): Promise<{ response: Response; result: unknown }> => {
    let response: Response
    try {
      response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
        ...(controller ? { signal: controller.signal } : {})
      })
    } catch {
      // 網路斷了、後端重啟中或等太久：登入還在，之後再試
      throw new TokenRefreshError(SESSION_RETRY_MESSAGE, true)
    }
    if (!response.ok) return { response, result: null }
    try {
      return { response, result: await response.json() }
    } catch {
      // 內容讀到一半斷掉或不是 JSON：登入可能還在（後端也許已經換好了，寬限期內再試拿得到同一組），之後再試
      throw new TokenRefreshError(SESSION_RETRY_MESSAGE, true)
    }
  }

  let response: Response
  let result: unknown
  try {
    ({ response, result } = await Promise.race([exchange(), timedOut]))
  } finally {
    if (timer) clearTimeout(timer)
  }

  if (!response.ok) {
    // 別的分頁可能剛換過一組（localStorage 共用）：本地已經是新的一組就直接用，不要當成登出
    const latestRefreshToken = localStorage.getItem('refreshToken')
    const latestAccessToken = localStorage.getItem('token')
    if (response.status === 401 && latestRefreshToken && latestRefreshToken !== refreshToken && latestAccessToken) {
      return latestAccessToken
    }
    // 5xx（含後端回的 503 HUB_UNAVAILABLE）與限流都是暫時的；其他（401／403）是登入失效
    const retryable = response.status >= 500 || response.status === 429
    throw new TokenRefreshError(retryable ? SESSION_RETRY_MESSAGE : 'Failed to refresh token', retryable)
  }

  const data = (result as { data?: { accessToken?: unknown; refreshToken?: unknown } } | null)?.data
  const newAccessToken = data?.accessToken
  const newRefreshToken = data?.refreshToken
  if (typeof newAccessToken !== 'string' || !newAccessToken) {
    throw new TokenRefreshError(SESSION_RETRY_MESSAGE, true)
  }

  // 回應晚到：送出之後別的分頁已經存了更新的一組（或已經登出），不能拿這組舊的蓋回去。
  // 蓋回去的話，一小時後續期用的是超過寬限期的舊 refresh token，後端會當成外流把整筆登入作廢
  const currentRefreshToken = localStorage.getItem('refreshToken')
  if (currentRefreshToken !== refreshToken) {
    const currentAccessToken = localStorage.getItem('token')
    if (currentRefreshToken && currentAccessToken) return currentAccessToken
    throw new TokenRefreshError('Logged out in another tab', false)
  }

  // 更新localStorage中的token
  localStorage.setItem('token', newAccessToken)
  if (typeof newRefreshToken === 'string' && newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken)
  }

  return newAccessToken
}

// 帶有自動刷新功能的API請求函數
export const apiRequestWithAutoRefresh = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data: T; message?: string }> => {
  const url = API_BASE_URL === '/api' && endpoint.startsWith('/api') 
    ? endpoint 
    : API_BASE_URL.endsWith('/api') && endpoint.startsWith('/api')
    ? endpoint
    : `${API_BASE_URL}${endpoint}`
  
  const makeRequest = async (headers: HeadersInit = {}) => {
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      },
      // GET 自動帶 nav signal（路由切換時取消）；其他方法不動，避免儲存被斷
      ...(isGetMethod(options) && !options.signal && { signal: getNavSignal() })
    }

    const response = await fetch(url, requestOptions)

    if (response.status === 401) {
      console.log('Received 401, attempting token refresh...')
      // 送出時那張已經被換掉就直接用新的；真的過期才續期。abort 原樣往上拋（caller 用 isAbortError 過濾）；
      // 中樞暫時連不上丟 503 不登出；登入確定失效才清 token 並強制登出。規則在 tokenAfterUnauthorized。
      const newToken = await tokenAfterUnauthorized(bearerTokenOf(headers))

      console.log('Token refresh successful, retrying request...')
      // 不沿用原 requestOptions.signal：refresh 期間若使用者切頁，舊 signal 已 abort，
      // retry 會立刻 throw AbortError，過去會被誤判為 refresh 失敗而強制登出。
      // caller 自帶 signal 仍尊重；其餘 GET 取一次最新的 nav signal。
      const retrySignal = options.signal
        ?? (isGetMethod(options) ? getNavSignal() : undefined)
      const retryResponse = await fetch(url, {
        ...requestOptions,
        headers: {
          ...headers,
          'Authorization': `Bearer ${newToken}`,
          ...options.headers
        },
        signal: retrySignal
      })
      return handleResponse(retryResponse)
    }

    return handleResponse(response)
  }

  return withRetry(async () => makeRequest(getAuthHeaders()))
}

// 通用 API 請求函數
export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data: T; message?: string }> => {
  // 如果 API_BASE_URL 是 '/api' 且 endpoint 已經以 '/api' 開頭，直接使用 endpoint
  // 否則正常拼接
  const url = API_BASE_URL === '/api' && endpoint.startsWith('/api')
    ? endpoint
    : API_BASE_URL.endsWith('/api') && endpoint.startsWith('/api')
    ? endpoint  // 避免重複的 /api
    : `${API_BASE_URL}${endpoint}`

  const defaultOptions: RequestInit = {
    headers: getAuthHeaders(),
    ...options,
    // GET 自動帶 nav signal（路由切換時取消）；其他方法不動，避免儲存被斷
    ...(isGetMethod(options) && !options.signal && { signal: getNavSignal() })
  }

  return withRetry(async () => {
    const response = await fetch(url, defaultOptions)
    return handleResponse(response)
  })
}

/**
 * 帶 Bearer token 的原始 fetch（不碰 Content-Type、不解析回應），401 時用共用鎖
 * 刷新 token 重試一次。
 *
 * 為什麼需要它：apiRequest / apiRequestWithAutoRefresh 固定送
 * `Content-Type: application/json` 並把回應當 JSON 解——上傳 FormData 會壞掉
 * multipart boundary，下載影像則需要 blob。身分證影本兩種都要。
 */
export const authedRawFetch = async (endpoint: string, init: RequestInit = {}): Promise<Response> => {
  const url = API_BASE_URL === '/api' || API_BASE_URL.endsWith('/api')
    ? endpoint
    : `${API_BASE_URL}${endpoint}`

  const withAuth = (token: string | null): RequestInit => ({
    ...init,
    headers: {
      ...(init.headers || {}),
      ...(token && { Authorization: `Bearer ${token}` })
    }
  })

  const sentToken = localStorage.getItem('token')
  const response = await fetch(url, withAuth(sentToken))
  if (response.status !== 401) return response

  // 送出時那張已經被換掉就直接用新的；真的過期才續期（中樞暫時連不上丟可重試的錯誤，不登出）
  const newToken = await tokenAfterUnauthorized(sentToken)
  return fetch(url, withAuth(newToken))
}

// 處理 API 錯誤
export const handleApiError = (error: any): Error => {
  if (error.response) {
    // Axios 錯誤格式
    const message = error.response.data?.message || error.response.data?.errors?.[0] || error.message
    return new Error(message)
  }
  
  if (error.message) {
    return new Error(error.message)
  }
  
  return new Error('發生未知錯誤')
}
